import React, { useCallback, useEffect, useState } from 'react';
import {
  Building2,
  Plus,
  Settings,
  CheckCircle,
  XCircle,
  Search,
  UserCircle,
  Pencil,
  Ban,
  CheckCircle2,
  KeyRound,
  SlidersHorizontal,
} from 'lucide-react';
import AppPageHeader from '../shell/AppPageHeader';
import PasswordResetLinkDialog from '../shared/PasswordResetLinkDialog';
import TableRowKebabMenu from '../shared/TableRowKebabMenu';
import {
  createClientLicense,
  fetchClientLicenses,
  issueClientLicenseAdminPasswordReset,
  setClientLicenseActive,
  updateClientLicense,
  type ClientLicenseDto,
} from '../../services/ClientLicensesApiClient';
import { shellMainPaddingX } from '../../utils/shellLayoutClasses';

export default function ClientLicenses() {
  const [licenses, setLicenses] = useState([] as ClientLicenseDto[]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null as string | null);
  const [actionError, setActionError] = useState(null as string | null);
  const [licensePanel, setLicensePanel] = useState('closed' as 'closed' | 'add' | 'edit');
  const [editingId, setEditingId] = useState(null as string | null);
  const [kebabForLicenseId, setKebabForLicenseId] = useState(null as string | null);
  const panelOpen = licensePanel !== 'closed';
  const [saving, setSaving] = useState(false);
  const [newFirm, setNewFirm] = useState({
    firmName: '',
    contactEmail: '',
    phone: '',
    address: '',
    maxAnalyses: 1000,
    expiresAt: '',
    adminPassword: '',
    adminPasswordConfirm: '',
    adminFirstName: '',
    adminLastName: '',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const [adminResetDialog, setAdminResetDialog] = useState(null as { email: string; passwordResetLink: string } | null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await fetchClientLicenses();
      setLicenses(data);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load licenses');
      setLicenses([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const searchLower = (searchQuery || '').trim().toLowerCase();
  const filteredLicenses = searchLower
    ? licenses.filter((l) => {
        const firm = (l.firmName || '').toLowerCase();
        const email = (l.contactEmail || '').toLowerCase();
        const phone = (l.phone || '').toLowerCase();
        const address = (l.address || '').toLowerCase();
        const max = String(l.maxAnalyses);
        const exp = (l.expiresAt || '').toLowerCase();
        const expDate = l.expiresAt && l.expiresAt.length >= 10 ? l.expiresAt.slice(0, 10).toLowerCase() : exp;
        const status = l.active ? 'active' : 'inactive';
        return (
          firm.includes(searchLower) ||
          email.includes(searchLower) ||
          phone.includes(searchLower) ||
          address.includes(searchLower) ||
          max.includes(searchLower) ||
          expDate.includes(searchLower) ||
          status.includes(searchLower)
        );
      })
    : licenses;

  const totalPages = Math.max(1, Math.ceil(filteredLicenses.length / pageSize));
  const paginatedLicenses = filteredLicenses.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, licenses.length]);

  const emptyFirmForm = () => ({
    firmName: '',
    contactEmail: '',
    phone: '',
    address: '',
    maxAnalyses: 1000,
    expiresAt: '',
    adminPassword: '',
    adminPasswordConfirm: '',
    adminFirstName: '',
    adminLastName: '',
  });

  const openAddPanel = () => {
    setActionError(null);
    setNewFirm(emptyFirmForm());
    setEditingId(null);
    setLicensePanel('add');
  };

  const openEditPanel = (l: ClientLicenseDto) => {
    setActionError(null);
    const exp =
      l.expiresAt && l.expiresAt.length >= 10 ? l.expiresAt.slice(0, 10) : l.expiresAt ? l.expiresAt.trim() : '';
    setNewFirm({
      firmName: l.firmName,
      contactEmail: l.contactEmail,
      phone: l.phone ?? '',
      address: l.address ?? '',
      maxAnalyses: l.maxAnalyses,
      expiresAt: exp,
      adminPassword: '',
      adminPasswordConfirm: '',
      adminFirstName: l.adminFirstName ?? '',
      adminLastName: l.adminLastName ?? '',
    });
    setEditingId(l.id);
    setLicensePanel('edit');
  };

  const closeLicensePanel = () => {
    if (!saving) {
      setLicensePanel('closed');
      setEditingId(null);
    }
  };

  const saveLicense = async () => {
    const firmName = newFirm.firmName.trim();
    const contactEmail = newFirm.contactEmail.trim();
    const phone = newFirm.phone.trim();
    const address = newFirm.address.trim();
    const adminPassword = newFirm.adminPassword;
    const adminPasswordConfirm = newFirm.adminPasswordConfirm;
    if (!firmName || !contactEmail || !phone || !address) {
      setActionError('Firm name, contact email, phone, and address are required.');
      return;
    }
    if (contactEmail.length > 100) {
      setActionError('Contact email must be 100 characters or fewer (used for firm admin login).');
      return;
    }

    if (licensePanel === 'add') {
      if (!adminPassword || adminPassword.length < 8) {
        setActionError('Firm admin password is required and must be at least 8 characters.');
        return;
      }
      if (adminPassword !== adminPasswordConfirm) {
        setActionError('Firm admin password and confirmation do not match.');
        return;
      }
    }

    setActionError(null);
    setSaving(true);
    try {
      if (licensePanel === 'edit' && editingId) {
        const updated = await updateClientLicense(editingId, {
          firmName,
          contactEmail,
          phone,
          address,
          maxAnalyses: newFirm.maxAnalyses,
          expiresAt: newFirm.expiresAt.trim() || null,
          ...(newFirm.adminFirstName.trim() ? { adminFirstName: newFirm.adminFirstName.trim() } : {}),
          ...(newFirm.adminLastName.trim() ? { adminLastName: newFirm.adminLastName.trim() } : {}),
        });
        setLicenses((prev) =>
          prev.map((x) => (x.id === editingId ? updated : x)).sort((a, b) => a.firmName.localeCompare(b.firmName))
        );
      } else {
        const created = await createClientLicense({
          firmName,
          contactEmail,
          phone,
          address,
          maxAnalyses: newFirm.maxAnalyses,
          expiresAt: newFirm.expiresAt.trim() || null,
          adminPassword: adminPassword!,
          adminFirstName: newFirm.adminFirstName.trim() || undefined,
          adminLastName: newFirm.adminLastName.trim() || undefined,
        });
        setLicenses((prev) => [...prev, created].sort((a, b) => a.firmName.localeCompare(b.firmName)));
      }
      setNewFirm(emptyFirmForm());
      setLicensePanel('closed');
      setEditingId(null);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to save license');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (id: string, nextActive: boolean) => {
    setActionError(null);
    try {
      const updated = await setClientLicenseActive(id, nextActive);
      setLicenses((prev) => prev.map((l) => (l.id === id ? updated : l)));
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to update license');
    }
  };

  const issueFirmAdminResetLink = async (l: ClientLicenseDto) => {
    setActionError(null);
    try {
      const { passwordResetLink } = await issueClientLicenseAdminPasswordReset(l);
      setAdminResetDialog({ email: l.contactEmail, passwordResetLink });
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to issue reset link.');
    }
  };

  const formatExpires = (iso: string | null) => {
    if (!iso || !iso.trim()) return 'No expiry';
    const t = iso.trim();
    const ymd = t.match(/^(\d{4}-\d{2}-\d{2})/);
    return ymd ? ymd[1] : t;
  };

  return (
    <>
      <main className="flex-1 flex flex-col min-h-0 min-w-0">
        <AppPageHeader title="Client Licenses" subtitle="Client (law firm) license configuration" />
        <div className={`flex-1 overflow-auto pb-8 pt-3 sm:pt-4 ${shellMainPaddingX}`}>
          <div className="w-full">
            {loadError && (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                {loadError}
              </div>
            )}
            {actionError && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
                {actionError}
              </div>
            )}

            {/* Stats */}
            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-700">
                    <Building2 size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-medium text-gray-900">{loading ? '—' : licenses.length}</p>
                    <p className="text-sm text-gray-500">Client firms</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
                    <CheckCircle size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-medium text-gray-900">
                      {loading ? '—' : licenses.filter((l) => l.active).length}
                    </p>
                    <p className="text-sm text-gray-500">Active licenses</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center text-amber-600">
                    <Settings size={20} />
                  </div>
                  <div>
                    <p className="text-2xl font-medium text-gray-900">
                      {loading ? '—' : licenses.reduce((s, l) => s + l.maxAnalyses, 0)}
                    </p>
                    <p className="text-sm text-gray-500">Total analysis slots</p>
                  </div>
                </div>
              </div>
            </div>

            {/* License list */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div
                className={`px-5 py-4 border-b border-gray-200 flex flex-wrap items-center gap-3 ${loading ? 'justify-end' : 'justify-between'}`}
              >
                {!loading && (
                  <div className="relative flex-1 min-w-[200px] max-w-md">
                    <Search
                      size={18}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                    />
                    <input
                      type="search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search firm, email, phone, address, status, max analyses, or expiry…"
                      className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg bg-white placeholder:text-gray-400 focus:ring-2 focus:ring-emerald-600/40 focus:border-emerald-600 outline-none"
                      aria-label="Search client licenses"
                    />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => openAddPanel()}
                  disabled={loading}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg btn-primary text-sm font-medium focus:ring-2 focus:ring-emerald-600/50 focus:ring-offset-2 disabled:opacity-50 flex-shrink-0"
                >
                  <Plus size={16} />
                  Add client
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[12rem]">
                        Firm
                      </th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[11rem]">
                        Contact
                      </th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Max analyses</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires</th>
                      <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-5 py-3 text-center whitespace-nowrap min-w-[7.5rem]" scope="col">
                        <span className="sr-only">Actions</span>
                        <SlidersHorizontal className="inline text-gray-400" size={16} aria-hidden />
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {loading ? (
                      <tr>
                        <td colSpan={6} className="px-5 py-8 text-center text-sm text-gray-500">
                          Loading licenses…
                        </td>
                      </tr>
                    ) : licenses.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-5 py-8 text-center text-sm text-gray-500">
                          No licenses yet. Add a client firm to get started.
                        </td>
                      </tr>
                    ) : filteredLicenses.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="px-5 py-8 text-center text-sm text-gray-500">
                          No licenses match your search.
                        </td>
                      </tr>
                    ) : (
                      paginatedLicenses.map((l) => (
                        <tr key={l.id} className="hover:bg-gray-50/50">
                          <td className="px-5 py-3 max-w-[20rem]">
                            <div className="font-medium text-gray-900">{l.firmName}</div>
                            {l.address ? (
                              <div className="mt-1 text-xs text-gray-500 line-clamp-2" title={l.address}>
                                {l.address}
                              </div>
                            ) : (
                              <div className="mt-1 text-xs text-gray-400">No address</div>
                            )}
                          </td>
                          <td className="px-5 py-3 max-w-[16rem]">
                            <div className="text-sm text-gray-600 break-all">{l.contactEmail}</div>
                            {l.phone ? (
                              <div className="mt-1 text-xs text-gray-500 whitespace-nowrap" title={l.phone}>
                                {l.phone}
                              </div>
                            ) : (
                              <div className="mt-1 text-xs text-gray-400">No phone</div>
                            )}
                          </td>
                          <td className="px-5 py-3 text-sm text-gray-600">{l.maxAnalyses}</td>
                          <td className="px-5 py-3 text-sm text-gray-600">{formatExpires(l.expiresAt)}</td>
                          <td className="px-5 py-3">
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                l.active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {l.active ? <CheckCircle size={12} /> : <XCircle size={12} />}
                              {l.active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-5 py-3 whitespace-nowrap align-middle">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                type="button"
                                onClick={() => openEditPanel(l)}
                                className="hidden sm:inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200/90 bg-white text-slate-600 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/45 focus-visible:ring-offset-1"
                                title="Edit license"
                                aria-label="Edit license"
                              >
                                <Pencil size={18} strokeWidth={2} aria-hidden />
                              </button>
                              <TableRowKebabMenu
                                ariaLabel={`Actions for ${l.firmName}`}
                                isOpen={kebabForLicenseId === l.id}
                                onOpenChange={(open) => setKebabForLicenseId(open ? l.id : null)}
                                entries={[
                                  {
                                    type: 'item',
                                    id: 'edit',
                                    label: 'Edit license',
                                    icon: <Pencil size={16} strokeWidth={2} aria-hidden />,
                                    onClick: () => openEditPanel(l),
                                    tone: 'default',
                                  },
                                  {
                                    type: 'item',
                                    id: 'reset',
                                    label: 'Issue firm admin reset link',
                                    icon: <KeyRound size={16} strokeWidth={2} aria-hidden />,
                                    onClick: () => void issueFirmAdminResetLink(l),
                                    tone: 'default',
                                  },
                                  { type: 'separator' },
                                  {
                                    type: 'item',
                                    id: 'toggle',
                                    label: l.active ? 'Deactivate' : 'Activate',
                                    icon: l.active ? (
                                      <Ban size={16} strokeWidth={2} aria-hidden />
                                    ) : (
                                      <CheckCircle2 size={16} strokeWidth={2} aria-hidden />
                                    ),
                                    onClick: () => toggleActive(l.id, !l.active),
                                    title: l.active ? 'Deactivate this firm license' : 'Activate this firm license',
                                    tone: l.active ? 'warning' : 'default',
                                  },
                                ]}
                              />
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              {!loading && licenses.length > 0 && filteredLicenses.length > pageSize && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50 text-sm text-gray-600">
                  <div>
                    Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(currentPage * pageSize, filteredLicenses.length)}</span> of{' '}
                    <span className="font-medium">{filteredLicenses.length}</span> licenses
                    {searchLower ? (
                      <>
                        {' '}
                        (<span className="font-medium">{licenses.length}</span> total)
                      </>
                    ) : null}
                  </div>
                  <div className="inline-flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1.5 border border-gray-300 rounded-l-md text-xs font-medium bg-white hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-1.5 text-xs border-y border-gray-300 bg-white">
                      Page <span className="font-medium">{currentPage}</span> of{' '}
                      <span className="font-medium">{totalPages}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1.5 border border-gray-300 rounded-r-md text-xs font-medium bg-white hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-white"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <PasswordResetLinkDialog
        open={adminResetDialog !== null}
        email={adminResetDialog?.email ?? ''}
        passwordResetLink={adminResetDialog?.passwordResetLink ?? ''}
        onClose={() => setAdminResetDialog(null)}
      />

      {/* Add client — right slide panel */}
      <div
        className={`fixed inset-0 z-50 transition-opacity duration-200 ${panelOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
        <div
          className="absolute inset-0 bg-black/40 transition-opacity duration-200"
          onClick={() => !saving && closeLicensePanel()}
          aria-hidden
        />
        <div
          className={`absolute top-0 right-0 h-full w-full max-w-2xl bg-white shadow-xl flex flex-col transition-transform duration-300 ease-out ${
            panelOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <form
            className="flex flex-col flex-1 min-h-0"
            onSubmit={(e) => {
              e.preventDefault();
              void saveLicense();
            }}
          >
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <h3 className="font-medium text-gray-900">
                {licensePanel === 'edit' ? 'Edit client (law firm)' : 'Add client (law firm)'}
              </h3>
              <button
                type="button"
                onClick={() => !saving && closeLicensePanel()}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <XCircle size={20} />
              </button>
            </div>
            <div className="p-6 space-y-8 overflow-y-auto flex-1">
              {actionError ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
                  {actionError}
                </div>
              ) : null}

              {/* Firm & license details */}
              <section aria-labelledby="add-license-firm-details-heading">
                <div className="flex items-start gap-3 mb-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
                    <Building2 size={20} aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <h4 id="add-license-firm-details-heading" className="text-sm font-medium text-gray-900">
                      Firm details
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5">Law firm identity, contact, and license limits.</p>
                  </div>
                </div>
                <div className="space-y-4 pl-0 sm:pl-[52px]">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="add-license-firm">
                      Firm name
                    </label>
                    <input
                      id="add-license-firm"
                      name="firmName"
                      type="text"
                      required
                      autoComplete="organization"
                      value={newFirm.firmName}
                      onChange={(e) => setNewFirm((p) => ({ ...p, firmName: e.target.value }))}
                      placeholder="Acme Law LLP"
                      className="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-600/40 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="add-license-email">
                      Contact email
                    </label>
                    <input
                      id="add-license-email"
                      name="contactEmail"
                      type="email"
                      required
                      autoComplete="email"
                      value={newFirm.contactEmail}
                      onChange={(e) => setNewFirm((p) => ({ ...p, contactEmail: e.target.value }))}
                      placeholder="admin@firm.com"
                      className="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-600/40 outline-none"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Also used as the firm admin login email (max 100 characters).
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="add-license-phone">
                      Phone
                    </label>
                    <input
                      id="add-license-phone"
                      name="phone"
                      type="tel"
                      required
                      autoComplete="tel"
                      value={newFirm.phone}
                      onChange={(e) => setNewFirm((p) => ({ ...p, phone: e.target.value }))}
                      placeholder="+1 555 0100"
                      className="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-600/40 outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="add-license-address">
                      Address
                    </label>
                    <textarea
                      id="add-license-address"
                      name="address"
                      required
                      rows={3}
                      autoComplete="street-address"
                      value={newFirm.address}
                      onChange={(e) => setNewFirm((p) => ({ ...p, address: e.target.value }))}
                      placeholder="Street, city, state, postal code"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-600/40 outline-none resize-y min-h-[5rem]"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="add-license-max">
                        Max analyses
                      </label>
                      <input
                        id="add-license-max"
                        name="maxAnalyses"
                        type="number"
                        min={1}
                        value={newFirm.maxAnalyses}
                        onChange={(e) => setNewFirm((p) => ({ ...p, maxAnalyses: Number(e.target.value) || 1000 }))}
                        className="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-600/40 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="add-license-expires">
                        Expires
                      </label>
                      <input
                        id="add-license-expires"
                        name="expiresAt"
                        type="date"
                        value={newFirm.expiresAt}
                        onChange={(e) => setNewFirm((p) => ({ ...p, expiresAt: e.target.value }))}
                        className="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-600/40 outline-none"
                      />
                    </div>
                  </div>
                </div>
              </section>

              {/* Firm admin user */}
              <section className="pt-6 border-t border-gray-200" aria-labelledby="add-license-admin-user-heading">
                <div className="flex items-start gap-3 mb-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
                    <UserCircle size={20} aria-hidden />
                  </span>
                  <div className="min-w-0">
                    <h4 id="add-license-admin-user-heading" className="text-sm font-medium text-gray-900">
                      Firm admin user
                    </h4>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {licensePanel === 'edit'
                        ? 'Firm admin signs in with the contact email. Use Reset password in the table to set a new password.'
                        : 'First user for this firm; they sign in with the contact email and password below.'}
                    </p>
                  </div>
                </div>
                <div className="space-y-4 pl-0 sm:pl-[52px]">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="add-license-admin-fn">
                        First name <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <input
                        id="add-license-admin-fn"
                        name="adminFirstName"
                        type="text"
                        autoComplete="given-name"
                        value={newFirm.adminFirstName}
                        onChange={(e) => setNewFirm((p) => ({ ...p, adminFirstName: e.target.value }))}
                        placeholder="Firm"
                        className="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-600/40 outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="add-license-admin-ln">
                        Last name <span className="text-gray-400 font-normal">(optional)</span>
                      </label>
                      <input
                        id="add-license-admin-ln"
                        name="adminLastName"
                        type="text"
                        autoComplete="family-name"
                        value={newFirm.adminLastName}
                        onChange={(e) => setNewFirm((p) => ({ ...p, adminLastName: e.target.value }))}
                        placeholder="Administrator"
                        className="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-600/40 outline-none"
                      />
                    </div>
                  </div>
                  {licensePanel === 'add' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="add-license-admin-pw">
                          Password
                        </label>
                        <input
                          id="add-license-admin-pw"
                          name="adminPassword"
                          type="password"
                          required
                          autoComplete="new-password"
                          value={newFirm.adminPassword}
                          onChange={(e) => setNewFirm((p) => ({ ...p, adminPassword: e.target.value }))}
                          placeholder="At least 8 characters"
                          className="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-600/40 outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="add-license-admin-pw2">
                          Confirm password
                        </label>
                        <input
                          id="add-license-admin-pw2"
                          name="adminPasswordConfirm"
                          type="password"
                          required
                          autoComplete="new-password"
                          value={newFirm.adminPasswordConfirm}
                          onChange={(e) => setNewFirm((p) => ({ ...p, adminPasswordConfirm: e.target.value }))}
                          className="w-full h-10 px-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-600/40 outline-none"
                        />
                      </div>
                    </>
                  )}
                </div>
              </section>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={() => !saving && closeLicensePanel()}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 rounded-lg btn-primary text-sm focus:ring-2 focus:ring-emerald-600/50 focus:ring-offset-2 disabled:opacity-50"
              >
                {saving ? 'Saving…' : licensePanel === 'edit' ? 'Save changes' : 'Add client'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
